// ═══════════════════════════════════════════════════════════
// PositionsGrid — Reusable positions table for all category pages
//
// Design principles:
//   1. Flat, consistent rows — every data row looks the same
//   2. Group headers are table rows with aggregated values
//   3. No indentation — detail rows are regular rows
//   4. Column headers + bold summary row always visible at top
//   5. Title + group-by toggle in slim top bar
//   6. One component for Supply, Demand, and Hedges
// ═══════════════════════════════════════════════════════════

import React, { useState, useMemo, useCallback } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { KeyboardArrowDown, KeyboardArrowRight, FileDownload } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { ToggleChips } from '../../pages/Dashboard/dashboard-components/ToggleChips';
import { useAppSession } from '../../contexts/AppSessionContext';
import { getGridSx } from './gridUtils';
import { exportToExcel, ExportColumn, resolveCurrency } from './exportExcel';

// ─── Types ──────────────────────────────────────────────────

export interface GroupByConfig {
  key: string;
  label?: string;
  modes: ('general' | 'formula')[];
  isDefaultFor?: 'general' | 'formula';
  /** Extract group key from a flat row. Omit for "None" (no grouping). */
  getGroupKey?: (row: Record<string, any>) => string;
  /** Display label shown on the group row */
  getGroupLabel?: (groupKey: string) => string;
  /** Summary text shown on the banner, e.g. "12 positions · 450 MT · $1.2M" */
  getGroupSummary?: (groupKey: string, childRows: Record<string, any>[]) => string;
  /** Optional accent color for the group row left border */
  getGroupColor?: (groupKey: string) => string;
}

export interface PositionsGridProps<T = any> {
  positions: T[];
  positionToRow: (pos: T, idx: number) => Record<string, any>;
  columns: GridColDef[];
  groupConfigs: GroupByConfig[];
  /** Build an aggregate row from a set of rows. Used for both the
   *  top-level summary and per-group sub-summaries. Return a row
   *  object with an `id` field. */
  buildSummaryRow: (rows: Record<string, any>[]) => Record<string, any>;
  categoryTerm: string;
  isGeneralMode: boolean;
  formulaId?: string;
  formulaName?: string;
  formulaColor?: string;
  uniqueFormulas?: number;
  loading?: boolean;
  children?: React.ReactNode;
  /** Override export columns. If omitted, derived from grid columns. */
  exportColumns?: ExportColumn[];
}

// ─── Row type markers ───────────────────────────────────────

const GROUP_ROW_PREFIX = '__group__';
const SUMMARY_ROW_CLASS = 'positions-grid-summary';
const GROUP_ROW_CLASS = 'positions-grid-group';

// ─── Styling ────────────────────────────────────────────────

const headerGridSx = {
  ...getGridSx(),
  borderRadius: 0,
  [`& .MuiDataGrid-row.${SUMMARY_ROW_CLASS}`]: {
    backgroundColor: '#f1f5f9',
    borderBottom: '2px solid #94a3b8',
    '& .MuiDataGrid-cell': {
      fontWeight: 700,
      fontSize: '0.8rem',
      color: '#0f172a',
      letterSpacing: '0.01em',
    },
    '&:hover': { backgroundColor: '#e2e8f0' },
  },
};

const bodyGridSx = {
  ...getGridSx(),
  borderRadius: 0,
  border: 'none',
  '& .MuiDataGrid-main': { borderTop: 'none' },
  '& .MuiDataGrid-columnHeaders': { display: 'none' },
  [`& .MuiDataGrid-row.${GROUP_ROW_CLASS}`]: {
    backgroundColor: '#f8fafc',
    cursor: 'pointer',
    '& .MuiDataGrid-cell': {
      fontWeight: 600,
      fontSize: '0.8rem',
      color: '#1e293b',
    },
    '&:hover': { backgroundColor: '#f1f5f9' },
  },
};

// ─── Component ──────────────────────────────────────────────

export function PositionsGrid<T = any>({
  positions,
  positionToRow,
  columns,
  groupConfigs,
  buildSummaryRow,
  categoryTerm,
  isGeneralMode,
  formulaId,
  formulaName,
  formulaColor = '#3b82f6',
  uniqueFormulas,
  loading,
  children,
  exportColumns,
}: PositionsGridProps<T>): React.ReactElement {
  const { user: sessionUser } = useAppSession();
  const accountName = sessionUser?.accountName || '';
  const mode: 'general' | 'formula' = isGeneralMode ? 'general' : 'formula';

  const availableConfigs = useMemo(
    () => groupConfigs.filter(c => c.modes.includes(mode)),
    [groupConfigs, mode],
  );

  const defaultGroupBy = useMemo(
    () => availableConfigs.find(c => c.isDefaultFor === mode)?.key || availableConfigs[0]?.key || 'None',
    [availableConfigs, mode],
  );

  const [groupBy, setGroupBy] = useState(defaultGroupBy);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  React.useEffect(() => {
    setGroupBy(defaultGroupBy);
    setExpandedGroups(new Set());
  }, [defaultGroupBy]);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  // ── Map positions to flat rows ────────────────────────────
  const flatRows = useMemo(
    () => positions.map((p, i) => positionToRow(p, i)),
    [positions, positionToRow],
  );

  // ── Top-level summary row ─────────────────────────────────
  const summaryRow = useMemo(
    () => buildSummaryRow(flatRows),
    [flatRows, buildSummaryRow],
  );

  // ── Columns wrapped for aggregate rows (blank empty cells) ──
  const blankEmptyCells = useCallback(
    (col: GridColDef): GridColDef => ({
      ...col,
      renderCell: col.renderCell
        ? (params: any) => (col.renderCell as any)(params)
        : (params: any) => {
            const v = params.value;
            if (v === '' || v === null || v === undefined) return <div />;
            return <div>{v}</div>;
          },
    }),
    [],
  );

  const summaryColumns = useMemo<GridColDef[]>(
    () => columns.map(blankEmptyCells),
    [columns, blankEmptyCells],
  );

  // ── Grouping logic ────────────────────────────────────────
  const activeConfig = availableConfigs.find(c => c.key === groupBy);
  const isGrouped = !!activeConfig?.getGroupKey;

  const groupedSections = useMemo(() => {
    if (!activeConfig?.getGroupKey) return null;

    const groups: Record<string, Record<string, any>[]> = {};
    const groupOrder: string[] = [];
    for (const row of flatRows) {
      const key = activeConfig.getGroupKey!(row);
      if (!groups[key]) {
        groups[key] = [];
        groupOrder.push(key);
      }
      groups[key].push(row);
    }

    return groupOrder.sort().map(key => ({
      key,
      label: activeConfig.getGroupLabel?.(key) || key,
      color: activeConfig.getGroupColor?.(key),
      rows: groups[key],
    }));
  }, [flatRows, activeConfig]);

  // ── Build body rows (group headers interleaved with details) ──
  const firstField = columns[0]?.field;

  const bodyRows = useMemo(() => {
    if (!isGrouped || !groupedSections) return flatRows;

    const result: Record<string, any>[] = [];
    for (const section of groupedSections) {
      const agg = buildSummaryRow(section.rows);
      result.push({
        ...agg,
        id: `${GROUP_ROW_PREFIX}${section.key}`,
        [firstField]: section.label,
        _isGroupRow: true,
        _groupKey: section.key,
        _groupColor: section.color,
        _groupCount: section.rows.length,
        _isExpanded: expandedGroups.has(section.key),
      });
      if (expandedGroups.has(section.key)) {
        result.push(...section.rows);
      }
    }
    return result;
  }, [isGrouped, groupedSections, expandedGroups, buildSummaryRow, firstField, flatRows]);

  // ── Body columns — first col shows arrow+label for group rows ──
  const bodyColumns = useMemo<GridColDef[]>(() => {
    return columns.map((col, idx) => {
      const wrapped = blankEmptyCells(col);
      if (idx === 0) {
        return {
          ...wrapped,
          renderCell: (params: any) => {
            const row = params.row;
            if (row._isGroupRow) {
              const Arrow = row._isExpanded ? KeyboardArrowDown : KeyboardArrowRight;
              return (
                <div className="flex items-center gap-2 w-full">
                  <Arrow sx={{ fontSize: 18, color: '#64748b' }} />
                  <span
                    className="w-1.5 h-4 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: row._groupColor || '#94a3b8' }}
                  />
                  <span className="font-semibold text-slate-800 truncate">{params.value}</span>
                  <span className="text-[11px] text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5 font-medium flex-shrink-0">
                    {row._groupCount}
                  </span>
                </div>
              );
            }
            if (col.renderCell) return (col.renderCell as any)(params);
            return <div>{params.value}</div>;
          },
        };
      }
      return {
        ...wrapped,
        renderCell: (params: any) => {
          if (params.row._isGroupRow) {
            if (col.renderCell) return (col.renderCell as any)(params);
            const v = params.value;
            if (v === '' || v === null || v === undefined) return <div />;
            return <div>{v}</div>;
          }
          return (wrapped.renderCell as any)(params);
        },
      };
    });
  }, [columns, blankEmptyCells]);

  // ── Handle row click (toggle group expansion) ─────────────
  const handleRowClick = useCallback(
    (params: any) => {
      if (params.row._isGroupRow) {
        toggleGroup(params.row._groupKey);
      }
    },
    [toggleGroup],
  );

  // ── Group-by chips ────────────────────────────────────────
  const chipOptions = useMemo(
    () => availableConfigs.map((c, i) => ({ key: c.label || c.key, value: i })),
    [availableConfigs],
  );
  const selectedChipIdx = availableConfigs.findIndex(c => c.key === groupBy);

  // ── Row class names ───────────────────────────────────────
  const getRowClassName = useCallback((params: any) => {
    if (params.row._isGroupRow) return GROUP_ROW_CLASS;
    if (params.row.isRatioPO) return 'positions-grid-ratio-po';
    if (params.row.isOutOfScope) return 'positions-grid-out-of-scope';
    return '';
  }, []);

  // ── Minimum table width from column minWidths ───────────
  const tableMinWidth = useMemo(
    () => columns.reduce((sum, col) => sum + (col.minWidth || 100), 0),
    [columns],
  );

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex flex-col bg-white p-3 gap-2 h-full overflow-auto">
      {/* ── Title + group-by toggle ── */}
      <div className="flex flex-row items-center gap-4 flex-shrink-0">
        <h3 className="text-sm font-semibold text-slate-800">
          {isGeneralMode
            ? `${categoryTerm} · ${uniqueFormulas ?? ''} formulas`
            : `${categoryTerm} · ${formulaName || formulaId || ''}`}
        </h3>

        {chipOptions.length > 0 && (
          <>
            <div className="w-px h-5 bg-slate-200" />
            <div className="flex flex-row items-center gap-2">
              <span className="text-xs text-slate-500">Group by</span>
              <ToggleChips
                chips={chipOptions}
                selected={selectedChipIdx >= 0 ? selectedChipIdx : 0}
                setSelected={(idx: number) => setGroupBy(availableConfigs[idx].key)}
                width="5.5rem"
              />
            </div>
          </>
        )}

        <Tooltip title="Export all positions to Excel" arrow enterDelay={400}>
          <button
            onClick={() => {
              const cols: ExportColumn[] = [];
              if (exportColumns) {
                cols.push(...exportColumns);
              } else {
                for (const c of columns) {
                  const field = c.field;
                  const header = typeof c.headerName === 'string' ? c.headerName : c.field;
                  const width = Math.round((c.minWidth || 100) / 8);
                  const isCurrency = /price|cost|value|total/i.test(field) && !/quantity/i.test(field);
                  const isDate = /period|date/i.test(field) && !/price/i.test(field);

                  if (isCurrency) {
                    // Raw number column (sortable/summable in Excel)
                    cols.push({ field, header, width });
                    // Companion currency code column
                    cols.push({
                      field: `__cur_${field}`,
                      header: 'Currency',
                      width: 10,
                      valueGetter: (row) => resolveCurrency(field, row, header),
                    });
                  } else if (isDate) {
                    // Readable date label
                    cols.push({
                      field: `__label_${field}`,
                      header,
                      width,
                      valueGetter: (row) => {
                        const val = row[field];
                        if (!val) return '';
                        const d = new Date(val);
                        return isNaN(d.getTime()) ? val : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                      },
                    });
                    // Native Excel date (sortable)
                    cols.push({
                      field,
                      header: `${header} (date)`,
                      width: 14,
                      valueGetter: (row) => {
                        const val = row[field];
                        if (!val) return '';
                        const d = new Date(val);
                        return isNaN(d.getTime()) ? val : d;
                      },
                    });
                  } else {
                    cols.push({ field, header, width });
                  }
                }
              }
              const acct = accountName.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-');
              const label = isGeneralMode ? categoryTerm : `${categoryTerm}-${formulaName || formulaId || ''}`;
              const safeName = label.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-');
              const date = new Date().toISOString().substring(0, 10);
              exportToExcel(flatRows, cols, `${acct ? acct + '-' : ''}${safeName}-${date}`);
            }}
            className="ml-auto p-1.5 rounded-md"
            style={{ transition: 'color 150ms' }}
          >
            <FileDownload sx={{ fontSize: 18, color: '#3b82f6', 'button:hover &': { color: '#1d4ed8' } }} />
          </button>
        </Tooltip>
      </div>

      {/* ── Table ── */}
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <div style={{ minWidth: tableMinWidth }}>
          {/* ── Column headers + top summary row ── */}
          <DataGrid
            rows={[summaryRow]}
            columns={summaryColumns}
            autoHeight
            disableRowSelectionOnClick
            disableColumnMenu
            hideFooter
            rowHeight={40}
            getRowClassName={() => SUMMARY_ROW_CLASS}
            sx={headerGridSx}
          />

          {/* ── Body: group rows + detail rows (single grid) ── */}
          {bodyRows.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No positions</div>
          ) : (
            <DataGrid
              rows={bodyRows}
              columns={bodyColumns}
              autoHeight
              loading={loading}
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooter
              columnHeaderHeight={0}
              onRowClick={handleRowClick}
              getRowClassName={getRowClassName}
              sx={bodyGridSx}
            />
          )}
        </div>
      </div>

      {children}
    </div>
  );
}
